import { useFirebase } from "./FirebaseProvider";
import { useState, useEffect, useRef } from "react";
import { useGlobalState } from "AppStore";
import getUUID from "utility/getUUID";
import moment from "moment";

export type CMRequest = {
  projectID: string;
  groupingType: "MINUTES" | "HOURS" | "DAYS" | "MONTHS" | "YEARS";
  timeFrom: number;
  timeTo: number;
  tagList: string[];
  usePath?: boolean;
};

export type CMTagData = {
  name: string;
  points: { time: number; value: number }[];
  tagId: string;
  unit: string;
};

export const useCMTag = (
  tag: string,
  fromUnix: number,
  toUnix: number,
  groupingType: CMRequest["groupingType"],
  usePath?: boolean,
  updateInterval?: number
) => {
  const { cmProjectID, projectLoaded } = useGlobalState();

  const firebase = useFirebase();
  const [cmData, setcmData] = useState<CMTagData | null>(null);
  const curCallID = useRef<string>("");

  const [loading, setLoading] = useState(false);
  const autoUpdating = useRef(false);

  const [time, setTime] = useState({ from: fromUnix, to: toUnix });

  //auto update the graph to load new data at an interval:
  useEffect(() => {
    setTime({ from: fromUnix, to: toUnix });

    const timeDifference = toUnix - fromUnix;
    let interval = updateInterval || timeDifference;
    if (interval < 60000) interval = 30000; // max query every 60 s
    if (interval > 3600000) interval = 3600000; // uodate atleast once per 1 hours
    const updateTime = () => {
      autoUpdating.current = true;
      const now = moment().valueOf();
      setTime({ from: now - timeDifference, to: now });
    };

    const intervalID = setInterval(updateTime, interval);
    return () => {
      clearInterval(intervalID);
    };
  }, [fromUnix, toUnix, updateInterval, tag]);

  useEffect(() => {
    if (!projectLoaded) return;
    if (!autoUpdating.current) setLoading(true);
    const req: CMRequest = {
      projectID: cmProjectID,
      groupingType,
      timeFrom: time.from,
      timeTo: time.to,
      tagList: [tag],
      usePath,
    };

    const callID = getUUID();
    curCallID.current = callID;

    autoUpdating.current = false; // always false unless just called by autoupdater
    const functions = firebase.app().functions("europe-west2");
    const getHistoricDataEU = functions.httpsCallable("getHistoricDataEU");
    getHistoricDataEU(req)
      .then((res) => {
        //check no recent new calls are made:
        if (curCallID.current === callID) {
          setcmData(res.data[0] as CMTagData);
          setLoading(false);
        }
      })
      .catch((error) => {
        if (curCallID.current === callID) {
          setcmData(null);
          setLoading(false);
          console.log(error);
        }
      });
  }, [tag, firebase, time, cmProjectID, groupingType, usePath, projectLoaded]);

  return { cmData, loading };
};

export const useCMTagList = (
  tagList: string[],
  fromUnix: number,
  toUnix: number,
  groupingType: CMRequest["groupingType"],
  usePath?: boolean,
  updateInterval?: number
) => {
  const { cmProjectID, projectLoaded } = useGlobalState();

  const firebase = useFirebase();
  const [cmData, setcmData] = useState<CMTagData[] | null>(null);
  const curCallID = useRef<string>("");
  const [loading, setLoading] = useState(false);
  const autoUpdating = useRef(false);

  const [time, setTime] = useState({ from: fromUnix, to: toUnix });

  //auto update the graph to load new data at an interval:
  useEffect(() => {
    setTime({ from: fromUnix, to: toUnix });

    const timeDifference = toUnix - fromUnix;
    let interval = updateInterval || timeDifference;
    if (interval < 60000) interval = 60000; // min query interval every 60 s
    if (interval > 3600000) interval = 3600000; // uodate atleast once per 1 hours
    const updateTime = () => {
      autoUpdating.current = true;
      const now = moment().valueOf();
      setTime({ from: now - timeDifference, to: now });
    };

    const intervalID = setInterval(updateTime, interval);
    return () => {
      clearInterval(intervalID);
    };
  }, [fromUnix, toUnix, updateInterval]);

  useEffect(() => {
    if (!projectLoaded || tagList.length === 0) return;
    if (!autoUpdating.current) setLoading(true);
    const req: CMRequest = {
      projectID: cmProjectID,
      timeFrom: time.from,
      timeTo: time.to,
      tagList,
      groupingType,
      usePath,
    };
    const callID = getUUID();
    curCallID.current = callID;

    autoUpdating.current = false; // always false unless just called by autoupdater..

    const functions = firebase.app().functions("europe-west2");
    const getHistoricDataEU = functions.httpsCallable("getHistoricDataEU");
    getHistoricDataEU(req)
      .then((res) => {
        if (curCallID.current === callID) {
          setcmData(res.data as CMTagData[]);
          setLoading(false);
        }
      })
      .catch((error) => {
        if (curCallID.current === callID) {
          setLoading(false);
          console.log(error);
        }
      });
  }, [tagList, time, groupingType, firebase, cmProjectID, usePath, projectLoaded]);

  return { cmData, loading };
};
